import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { Calendar, Clock, Phone, User, FileText, ChevronLeft, ChevronRight, Loader2, Check, X, DollarSign, CalendarCheck, Star, CreditCard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Calendar Component
const SimpleCalendar = ({ selectedDate, onSelectDate, minDate, lang }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const tr = t(lang);
  
  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();
  
  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();
  
  const days = [];
  
  // Day names by language
  const dayNamesByLang = {
    en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    ht: ['Dim', 'Len', 'Mad', 'Mèk', 'Jed', 'Van', 'Sam'],
    fr: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
    es: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
  };
  
  const monthNamesByLang = {
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    ht: ['Janvye', 'Fevriye', 'Mas', 'Avril', 'Me', 'Jen', 'Jiyè', 'Out', 'Septanm', 'Oktòb', 'Novanm', 'Desanm'],
    fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
    es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  };
  
  const dayNames = dayNamesByLang[lang] || dayNamesByLang.en;
  const monthNames = monthNamesByLang[lang] || monthNamesByLang.en;
  
  // Empty cells for days before the first of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  
  // Days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  
  const formatDate = (day) => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return d.toISOString().split('T')[0];
  };
  
  const isDisabled = (day) => {
    if (!day) return true;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  
  return (
    <div className="p-3 bg-secondary/30 rounded-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="icon" onClick={prevMonth}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="font-semibold">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <Button variant="ghost" size="icon" onClick={nextMonth}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs text-muted-foreground font-medium py-1">
            {day}
          </div>
        ))}
      </div>
      
      {/* Days */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => (
          <button
            key={idx}
            disabled={isDisabled(day)}
            onClick={() => day && onSelectDate(formatDate(day))}
            className={cn(
              "h-9 w-9 rounded-lg text-sm transition-all",
              !day && "invisible",
              day && !isDisabled(day) && "hover:bg-pink-500/20",
              day && isDisabled(day) && "text-muted-foreground/50 cursor-not-allowed",
              selectedDate === formatDate(day) && "bg-pink-500 text-white hover:bg-pink-600"
            )}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  );
};

// Time Slot Picker
const TimeSlotPicker = ({ slots, selectedTime, onSelectTime, loading, lang }) => {
  const tr = t(lang);
  
  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
      </div>
    );
  }
  
  if (!slots || slots.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground text-sm">
        {tr('noSlotsAvailable')}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-4 gap-2 max-h-[200px] overflow-y-auto p-1">
      {slots.map(slot => (
        <button
          key={slot}
          onClick={() => onSelectTime(slot)}
          className={cn(
            "py-2 px-3 rounded-lg text-sm font-medium transition-all",
            selectedTime === slot 
              ? "bg-pink-500 text-white" 
              : "bg-secondary hover:bg-pink-500/20"
          )}
        >
          {slot}
        </button>
      ))}
    </div>
  );
};

// PayPal Button Component
const PayPalButton = ({ amount, onSuccess, onError, loading }) => {
  const handlePayPal = async () => {
    try {
      // Open PayPal in a new window
      const paypalUrl = `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=fityhapp@gmail.com&item_name=Appointment+Booking&amount=${amount}&currency_code=USD&return=${encodeURIComponent(window.location.origin + '/appointments?payment=success')}&cancel_return=${encodeURIComponent(window.location.origin + '/appointments?payment=cancelled')}`;
      
      window.open(paypalUrl, '_blank', 'width=600,height=700');
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  };
  
  return (
    <Button
      type="button"
      onClick={handlePayPal}
      disabled={loading}
      className="w-full bg-[#0070ba] hover:bg-[#003087] text-white"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <>
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082h-2.19c-1.717 0-3.2 1.255-3.456 2.96l-1.12 7.106-.457 2.9a.641.641 0 0 0 .633.74h4.606c.524 0 .968-.382 1.05-.9l.86-5.452c.082-.518.526-.9 1.05-.9h.662c4.298 0 7.664-1.746 8.647-6.796.395-2.026.09-3.62-1.079-4.453z"/>
          </svg>
          PayPal
        </>
      )}
    </Button>
  );
};

// Booking Modal
export const BookingModal = ({ open, onClose, provider, service, onSuccess }) => {
  const { user, token, language } = useAuth();
  const tr = t(language);
  const [step, setStep] = useState(1); // 1: Date/Time, 2: Details, 3: Confirm, 4: Payment
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(null); // 'paypal', 'card', 'later'
  const [form, setForm] = useState({
    date: '',
    time: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    notes: ''
  });
  
  // Reset form when opening
  useEffect(() => {
    if (open) {
      setStep(1);
      setForm({
        date: '',
        time: '',
        client_name: user?.display_name || user?.username || '',
        client_email: user?.email || '',
        client_phone: '',
        notes: ''
      });
      setSlots([]);
    }
  }, [open, user]);
  
  // Fetch available slots when date changes
  useEffect(() => {
    if (form.date && provider?.id && service?.id) {
      fetchSlots();
    }
  }, [form.date]);
  
  const fetchSlots = async () => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(
        `${API}/appointments/slots/${provider.id}/${form.date}?service_id=${service.id}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSlots(response.data.slots || []);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };
  
  const handleSubmit = async (withPayment = false) => {
    if (!form.client_name || !form.client_phone || !form.client_email) {
      toast.error(tr('fillAllFields'));
      return;
    }
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.client_email)) {
      toast.error(tr('invalidEmail'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        service_id: service.id,
        provider_id: provider.id,
        date: form.date,
        time: form.time,
        client_name: form.client_name,
        client_email: form.client_email,
        client_phone: form.client_phone,
        notes: form.notes,
        payment_method: paymentMethod,
        payment_status: withPayment ? 'pending' : 'unpaid'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(tr('appointmentCreated'));
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || tr('cannotCreateAppointment'));
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const localeMap = { en: 'en-US', ht: 'ht-HT', fr: 'fr-FR', es: 'es-ES' };
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(localeMap[language] || 'en-US', options);
  };
  
  // Check if service requires payment
  const requiresPayment = service && !service.is_free && service.price > 0;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-pink-500" />
            {tr('bookAppointment')}
          </DialogTitle>
        </DialogHeader>
        
        {/* Service Info */}
        <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-xl mb-4">
          <Avatar className="w-10 h-10">
            <AvatarImage src={provider?.avatar} />
            <AvatarFallback>{provider?.username?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{service?.name}</p>
            <p className="text-xs text-muted-foreground">
              {service?.duration_minutes} {tr('minutes')} • {service?.is_free ? tr('free') : `$${service?.price}`}
            </p>
          </div>
        </div>
        
        {/* Step 1: Date & Time */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4" />
                {tr('chooseDate')}
              </Label>
              <SimpleCalendar
                selectedDate={form.date}
                onSelectDate={(date) => setForm({...form, date, time: ''})}
                lang={language}
              />
            </div>
            
            {form.date && (
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4" />
                  {tr('chooseTime')}
                </Label>
                <TimeSlotPicker
                  slots={slots}
                  selectedTime={form.time}
                  onSelectTime={(time) => setForm({...form, time})}
                  loading={slotsLoading}
                  lang={language}
                />
              </div>
            )}
            
            <Button 
              className="w-full" 
              disabled={!form.date || !form.time}
              onClick={() => setStep(2)}
            >
              {tr('continueBtn')}
            </Button>
          </div>
        )}
        
        {/* Step 2: Client Details */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="p-3 bg-pink-500/10 rounded-lg text-sm">
              <p className="font-medium">{formatDate(form.date)}</p>
              <p className="text-muted-foreground">@ {form.time}</p>
            </div>
            
            <div>
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {tr('fullName')} *
              </Label>
              <Input
                value={form.client_name}
                onChange={(e) => setForm({...form, client_name: e.target.value})}
                placeholder={tr('name')}
                data-testid="booking-name-input"
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                {tr('emailAddress')} *
              </Label>
              <Input
                type="email"
                value={form.client_email}
                onChange={(e) => setForm({...form, client_email: e.target.value})}
                placeholder="email@example.com"
                data-testid="booking-email-input"
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {tr('phoneNumber')} *
              </Label>
              <Input
                value={form.client_phone}
                onChange={(e) => setForm({...form, client_phone: e.target.value})}
                placeholder="+509 ..."
                data-testid="booking-phone-input"
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {tr('notesOptional')}
              </Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder={tr('messageToProvider')}
                rows={3}
              />
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>
                {tr('back')}
              </Button>
              <Button 
                className="flex-1" 
                onClick={() => setStep(3)}
                disabled={!form.client_name || !form.client_phone || !form.client_email}
              >
                {tr('continueBtn')}
              </Button>
            </div>
          </div>
        )}
        
        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="p-4 bg-secondary/50 rounded-xl space-y-3">
              <h4 className="font-semibold">{tr('appointmentSummary')}</h4>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tr('service')}:</span>
                  <span className="font-medium">{service?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tr('date')}:</span>
                  <span className="font-medium">{formatDate(form.date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tr('time')}:</span>
                  <span className="font-medium">{form.time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tr('duration')}:</span>
                  <span className="font-medium">{service?.duration_minutes} {tr('minutes')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tr('price')}:</span>
                  <span className="font-bold text-pink-500">
                    {service?.is_free ? tr('free') : `$${service?.price}`}
                  </span>
                </div>
              </div>
              
              <div className="pt-2 border-t space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tr('name')}:</span>
                  <span>{form.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span>{form.client_email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tr('phone')}:</span>
                  <span>{form.client_phone}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep(2)}>
                {tr('back')}
              </Button>
              {requiresPayment ? (
                <Button 
                  className="flex-1" 
                  onClick={() => setStep(4)}
                  data-testid="proceed-payment-btn"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  {tr('proceedToPayment')}
                </Button>
              ) : (
                <Button 
                  className="flex-1" 
                  onClick={() => handleSubmit(false)}
                  disabled={loading}
                  data-testid="confirm-booking-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {tr('confirmBooking')}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Step 4: Payment Options */}
        {step === 4 && requiresPayment && (
          <div className="space-y-4">
            <div className="p-4 bg-pink-500/10 rounded-xl text-center">
              <DollarSign className="w-8 h-8 text-pink-500 mx-auto mb-2" />
              <p className="font-semibold text-lg">${service?.price}</p>
              <p className="text-sm text-muted-foreground">{tr('paymentRequired')}</p>
            </div>
            
            <div className="space-y-3">
              {/* PayPal Option */}
              <button
                onClick={() => setPaymentMethod('paypal')}
                className={cn(
                  "w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3",
                  paymentMethod === 'paypal' 
                    ? "border-pink-500 bg-pink-500/10" 
                    : "border-border hover:border-pink-500/50"
                )}
              >
                <div className="w-10 h-10 rounded-full bg-[#0070ba] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">{tr('payWithPayPal')}</p>
                  <p className="text-xs text-muted-foreground">PayPal, Debit, Credit Card</p>
                </div>
                {paymentMethod === 'paypal' && (
                  <Check className="w-5 h-5 text-pink-500" />
                )}
              </button>
              
              {/* Card Option */}
              <button
                onClick={() => setPaymentMethod('card')}
                className={cn(
                  "w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3",
                  paymentMethod === 'card' 
                    ? "border-pink-500 bg-pink-500/10" 
                    : "border-border hover:border-pink-500/50"
                )}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">{tr('payWithCard')}</p>
                  <p className="text-xs text-muted-foreground">Visa, Mastercard, Amex</p>
                </div>
                {paymentMethod === 'card' && (
                  <Check className="w-5 h-5 text-pink-500" />
                )}
              </button>
              
              {/* Pay Later Option */}
              <button
                onClick={() => setPaymentMethod('later')}
                className={cn(
                  "w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3",
                  paymentMethod === 'later' 
                    ? "border-pink-500 bg-pink-500/10" 
                    : "border-border hover:border-pink-500/50"
                )}
              >
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">{tr('payLater')}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'ht' ? 'Peye lè ou rive' : language === 'fr' ? 'Payer à l\'arrivée' : language === 'es' ? 'Pagar al llegar' : 'Pay when you arrive'}
                  </p>
                </div>
                {paymentMethod === 'later' && (
                  <Check className="w-5 h-5 text-pink-500" />
                )}
              </button>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep(3)}>
                {tr('back')}
              </Button>
              {paymentMethod === 'paypal' ? (
                <PayPalButton
                  amount={service?.price}
                  onSuccess={() => handleSubmit(true)}
                  onError={() => toast.error(tr('paymentFailed'))}
                  loading={loading}
                />
              ) : (
                <Button 
                  className="flex-1" 
                  onClick={() => {
                    if (paymentMethod === 'card') {
                      toast.info(language === 'ht' ? 'Peman kat ap vini byento!' : 'Card payment coming soon!');
                      return;
                    }
                    handleSubmit(false);
                  }}
                  disabled={!paymentMethod || loading}
                  data-testid="complete-booking-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {tr('confirmBooking')}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
